import { Student } from './student.model';
import { LanguageCode } from './language.model';
import { DocumentReference, Timestamp } from 'firebase/firestore';

/**
 * Eine gebuchte Ferienwoche, wie sie in `selectedWeeks` einer Anmeldung liegt.
 *
 * Achtung: In Firestore stehen zwei Feldnamen-Varianten nebeneinander (#981).
 * Die reguläre Anmeldung über das Elternportal legt `startDate`/`endDate` an
 * (die Customer-UI mappt beim Aufruf der Function bewusst um), die
 * Admin-Umbuchung (#956) dagegen `start`/`end`. Beide Paare sind deshalb
 * optional — wer die Zeitpunkte braucht, muss beide Varianten lesen und darf
 * sich auf keine verlassen. `weekNumber` gibt es in beiden.
 */
export interface HolidayCareSelectedWeek {
  weekNumber: number;
  start?: Timestamp;
  end?: Timestamp;
  startDate?: Timestamp;
  endDate?: Timestamp;
  /** Mittagessen für diese Woche gebucht (nur wirksam, wenn die Woche `lunchAvailable` hat). */
  lunch?: boolean;
  /**
   * Standort-Snapshot zum Buchungszeitpunkt. `undefined` = eigene Schule,
   * `null` = Standort war bei der Buchung noch offen.
   */
  location?: HolidayCareLocation | null;
  /** Über eine Nachmelde-Freigabe gebucht (nach Ablauf der regulären Frist). */
  lateRegistration?: boolean;
}

/**
 * Standort einer Ferienwoche — eine Schule, deren Name als Snapshot mitgeführt
 * wird (wie `schoolName` in der Anmeldung), damit Umbenennungen die Historie
 * nicht verändern (#1063).
 */
export interface HolidayCareLocation {
  schoolId: string;
  schoolName: string;
}

/** Belegungsstatus einer Ferienwoche im Dashboard nach Standort (#1063). */
export type HolidayCareWeekStatus =
  | 'offen'
  | 'knapp'
  | 'ausgebucht'
  | 'nachmeldung';

/**
 * Freigabe einer Nachmeldung für eine Woche an einem Standort. Wird nur von der
 * Function `openHolidayCareLateRegistration` geschrieben, Auth-Nutzer lesen.
 * Dokument-ID: `lateRegistrationDocId(...)`.
 */
export interface HolidayCareLateRegistration {
  /** Kalenderjahr wie die ID des Konfig-Dokuments `schools/{id}/holidayConfigurations/{jahr}`. */
  schoolYear: number;
  vacationType: VacationType;
  weekNumber: number;
  locationSchoolId: string;
  locationSchoolName: string;
  openUntil: Timestamp;
  openedOn: Timestamp;
  openedBy: string;
  openedByEmail: string;
  /** Schulen, deren Eltern über die Freigabe informiert wurden. */
  notifiedSchoolIds: string[];
  notifiedParents: number;
}

export const HOLIDAY_CARE_LATE_REGISTRATIONS_COLLECTION =
  'holidayCareLateRegistrations';

/** Muss mit der Kopie in `functions/src/_shared/models.ts` übereinstimmen. */
export function lateRegistrationDocId(
  schoolYear: number,
  vacationType: VacationType,
  weekNumber: number,
  locationSchoolId: string
): string {
  return `${schoolYear}-${vacationType}-${weekNumber}-${locationSchoolId}`;
}

/** Mindestteilnehmer je Woche für neu angelegte Ferienzeiten (#1063). */
export const DEFAULT_MIN_PARTICIPANTS = 13;
/** Nachmeldefrist in Tagen für neu angelegte Ferienzeiten (#1063). */
export const DEFAULT_LATE_REGISTRATION_DAYS = 14;

export interface HolidayCareRegistration {
  id?: string;
  studentId: string;
  studentRef?: DocumentReference<Student>; // Reference to student document
  studentInfo: Partial<Student>; // Keep as fallback in case student gets deleted
  // Legacy program fields removed - now using vacationType
  schoolId: string;
  schoolName: string;
  originSchoolId?: string; // Herkunftsschule des Schülers, falls über Verbund-Fallback angemeldet
  originSchoolName?: string;
  userId: string;
  userEmail: string;
  status: HolidayCareRegistrationStatus;
  createdOn: Timestamp;
  updatedOn: Timestamp;
  cancelledOn?: Timestamp;
  updatedBy?: {
    uid?: string;
    email?: string;
  };
  language: LanguageCode;
  selectedWeeks: HolidayCareSelectedWeek[];
  /** Gesamtkosten (Gebühr + Mittagessen), serverseitig gerechnet. */
  calculatedCost: number;
  /** Aufschlüsselung der Gesamtkosten; fehlt bei Anmeldungen vor #1063. */
  costBreakdown?: { care: number; lunch: number };
  // New fields for vacation-based registrations
  vacationType?: VacationType;
}

export type HolidayCareRegistrationStatus =
  | 'draft'
  | 'open'
  | 'inactive'
  | 'closed'
  | 'cancelled';

export interface HolidayCareRegistrationStatusConfig {
  label: string;
  action: string;
  type: 'info' | 'warning' | 'success' | 'error';
  styles: {
    backgroundColor: string;
    color: string;
  };
}

export const HolidayCareRegistrationStatusConfigs: Record<
  HolidayCareRegistrationStatus,
  HolidayCareRegistrationStatusConfig
> = {
  draft: {
    label: $localize`:@@holidayCare.registrationStatus.draft:Entwurf`,
    action: $localize`:@@holidayCare.registrationStatus.draftAction:Ihre Anmeldung ist ein Entwurf.`,
    type: 'info',
    styles: {
      backgroundColor: '#ff8f00',
      color: '#000',
    },
  },
  open: {
    label: $localize`:@@holidayCare.registrationStatus.active:Offen`,
    action: $localize`:@@holidayCare.registrationStatus.activeAction:Die Anmeldung ist offen.`,
    type: 'error',
    styles: {
      backgroundColor: '#08acd3',
      color: '#000',
    },
  },
  inactive: {
    label: $localize`:@@holidayCare.registrationStatus.inactive:Inaktiv`,
    action: $localize`:@@holidayCare.registrationStatus.inactiveAction:Die Anmeldung ist inaktiv.`,
    type: 'error',
    styles: {
      backgroundColor: '#fffbeb',
      color: '#000',
    },
  },
  closed: {
    label: $localize`:@@holidayCare.registrationStatus.closed:Geschlossen`,
    action: $localize`:@@holidayCare.registrationStatus.closedAction:Anmeldung geschlossen.`,
    type: 'success',
    styles: {
      backgroundColor: '#ffebee',
      color: '#000',
    },
  },
  cancelled: {
    label: $localize`:@@holidayCare.registrationStatus.cancelled:Abgemeldet`,
    action: $localize`:@@holidayCare.registrationStatus.cancelledAction:Die Anmeldung wurde storniert.`,
    type: 'error',
    styles: {
      backgroundColor: '#f44336',
      color: '#fff',
    },
  },
};

export const HolidayCareRegistrationStatusOptions = [
  { value: '', label: $localize`:@@holidayCare.registrationStatus.all:Alle` },
  ...Object.entries(HolidayCareRegistrationStatusConfigs).map(
    ([value, config]) => ({
      value,
      label: config.label,
    })
  ),
];

export type VacationType = 'summer' | 'autumn' | 'winter' | 'easter';

export interface VacationWeek {
  weekNumber: number;
  start: Timestamp;
  end: Timestamp;
  enabled: boolean;
  /**
   * Standort der Woche. `undefined` (Feld fehlt) = eigene Schule,
   * `null` = noch nicht festgelegt, sonst die Standort-Schule (#1063).
   */
  location?: HolidayCareLocation | null;
  /** Mittagessen in dieser Woche buchbar. Default false. */
  lunchAvailable?: boolean;
}

export interface VacationPeriodConfiguration {
  vacationType: VacationType;
  name: string;
  enabled: boolean;
  info?: string; // Optional info text for cooperation locations, etc.
  dateRange: {
    start: Timestamp;
    end: Timestamp;
  };
  availableWeeks: VacationWeek[];
  selectedWeeks: VacationWeek[];
  /** Zuletzt veröffentlicht (#1257); nur im veröffentlichten Dokument gepflegt. */
  publishedOn?: Timestamp;
  publishedBy?: string;
  publishedByEmail?: string;
  settings: {
    /** UI-Label „Gebühr pro Woche". */
    costPerWeek: number;
    /** Preis Mittagessen je Woche; 0 oder fehlend = kein Essen buchbar (#1063). */
    lunchCostPerWeek?: number;
    /** Nachmeldefrist in Tagen; fehlend = DEFAULT_LATE_REGISTRATION_DAYS (#1063). */
    lateRegistrationDays?: number;
    minParticipants: number;
    maxParticipants?: number;
    registrationDeadline: Timestamp;
  };
}

export interface SchoolYearHolidayConfiguration {
  id?: string;
  schoolId: string;
  // calendarYear is now the document ID, no longer needed as a field
  vacationPeriods: VacationPeriodConfiguration[];
  createdOn: Timestamp;
  updatedOn: Timestamp;
  /** Audit (#1257), gesetzt von der employee-ui beim Speichern bzw. Veröffentlichen. */
  lastModifiedBy?: string;
  lastModifiedByEmail?: string;
  lastModifiedSource?: string;
}

export interface VacationPeriodData {
  name: string;
  type: VacationType;
  start: Date;
  end: Date;
  weeks: {
    weekNumber: number;
    start: Date;
    end: Date;
  }[];
}
